import {typeOf, http_build_query, trimSlashes, isObject} from './../utils'
import {ResponseDTO, API_RESPONSE_STATUS_SUCCESS, API_RESPONSE_STATUS_ERROR} from './ResponseDTO'
import {APIServiceError, API_ERROR_TYPE_NORMAL, API_ERROR_TYPE_INTERNAL, API_ERROR_TYPE_HIDDEN, API_ERROR_TYPE_REDIRECT} from './APIServiceError'

export class APIService
{
    constructor (
        fetch = null,
        onBeforeResponseValidation = null,
        onBeforeRequest = null,
        onAfterRequest = null,
        onErrorNotifyCallback = null,
        onRedirect = null,
        prepareUrl = null
    ) {
        this.fetch = (null !== fetch)
            ? fetch
            : () => {
                new Promise((resolve, reject) => {
                    reject(new APIServiceError('[APIService] Please provide "fetch" promise callback.'))
                })
            }

        let setCallback = function (callback) {
            return (null !== callback && callback !== undefined) ? callback : function(){}
        }

        this.onBeforeResponseValidation = setCallback(onBeforeResponseValidation)
        this.onBeforeRequest = setCallback(onBeforeRequest)
        this.onAfterRequest = setCallback(onAfterRequest)
        this.onErrorNotifyCallback = setCallback(onErrorNotifyCallback)
        this.onRedirect = setCallback(onRedirect)
        this.prepareUrl = setCallback(prepareUrl)
    }

    GET( api, method = null, params = {}, options = {} ) {
        return this.request( api, method, params, 'GET', options )
    }

    POST( api, method = null, params = {}, options = {} ) {
        return this.request( api, method, params, 'POST', options )
    }

    PUT( api, method = null, params = {}, options = {} ) {
        return this.request( api, method, params, 'PUT', options )
    }

    PATCH( api, method = null, params = {}, options = {} ) {
        return this.request( api, method, params, 'PATCH', options )
    }

    DELETE( api, method = null, params = {}, options = {} ) {
        return this.request( api, method, params, 'DELETE', options )
    }

    /**
     * Make API request
     *
     * @param api string API name
     * @param method string|null API function name
     * @param params object Request parameters
     * @param type Type of the request POST, PUT, DELETE and etc
     * @param options Some additional options to the request.
     *                For example you can send additional headers (see @php base-utils api component).
     *                Also you can provide here "onErrorNotifyCallback", "onBeforeRequest" and "onAfterRequest" callbacks
     *                that can change the default behavior.
     * @returns {*} ResponseDTO object with data as a Collection tree
     */
    request (
        api,
        method = null,
        params = {},
        type = 'POST',
        options = {}
    ) {
        let getCallback = (opt) => {
            let result = (undefined !== options[opt])
                ? options[opt]
                : this[opt]

            delete options[opt]
            return result
        }

        // this callbacks are outside of the try because we want to use them in the catch
        let onBeforeResponseValidation = getCallback('onBeforeResponseValidation')
        let onErrorNotifyCallback = getCallback('onErrorNotifyCallback')
        let onBeforeRequest = getCallback('onBeforeRequest')
        let onAfterRequest = getCallback('onAfterRequest')

        try {
            let headersJS = (undefined !== options.headersJS)
                ? options.headersJS
                : {}

            let encodeRequestParams = (undefined !== options.encodeRequestParams)
                ? options.encodeRequestParams
                : true

            // removed special options
            delete options.headersJS
            delete options.encodeRequestParams

            let queryParams = {}

            if ( typeOf(method) === 'object' ) {
                queryParams = cloneObject(method)
                delete queryParams.method

                method = method.method === undefined ? null : method.method
            }

            let requestParams = {
                api,
                method,
                params,
                type,
                options,
                queryParams
            }

            onBeforeRequest(requestParams)

            return this._prepareUrl(requestParams)
                .then(
                    requestUrl => this.fetch(requestUrl, params, headersJS, encodeRequestParams, requestParams)
                )
                .then(
                    response => this._validateResponse(response, requestParams, onBeforeResponseValidation)
                )
                .then(
                    response => {
                        let modifiedResponse = onAfterRequest(requestParams, response, false)
                        if( undefined !== modifiedResponse ) {
                            return modifiedResponse
                        }

                        return response
                    }
                )
                .catch(
                    exception => {
                        exception = this._getAPIServiceError(exception)

                        try {
                            let modifiedResponse = onAfterRequest(requestParams, exception.getResponse(), exception)
                            if( undefined !== modifiedResponse ) {
                                exception.response = modifiedResponse
                            }
                        }
                        catch ( error ) {
                            exception = this._getAPIServiceError( error )
                        }

                        onErrorNotifyCallback(exception)

                        throw exception
                    }
                )
        }
        catch ( error ) {
            // ---------------------------------------------------------- //
            // - catch all developer errors here                          //
            // - this try/catch block holds all of the promises           //
            // - all promise errors will be handled in its promise catch  //
            //   at the right time of the execution!                      //
            // ---------------------------------------------------------- //

            let exception = this._getAPIServiceError(error)

            try {
                onErrorNotifyCallback(exception)
            }
            catch ( error ) {
                exception = this._getAPIServiceError(error)
            }

            exception.message = '[APIService] Development Error: ' + exception.getMessage()

            return Promise.reject(exception)
        }
    }

    _prepareUrl (requestParams)
    {
        let { api, method, type, options, queryParams } = requestParams

        return new Promise((resolve, reject) => {

            let prepareUrlResponse = this.prepareUrl(requestParams)

            if( prepareUrlResponse !== undefined ) {
                return resolve(prepareUrlResponse)
            }

            if( isEmpty(api) ) {
                return reject(new APIServiceError('[APIService] Parameter "api" of API::request() is required!'))
            }

            let requestUrl = '/' + trimSlashes(api)

            if( ! isEmpty(method) ) {
                requestUrl += '/' + trimSlashes(method)
            }

            let params = cloneObject(queryParams)

            params['$type'] = type

            if( ! isEmpty(options) ) {
                params['$options'] = options
            }

            params['oc-vue-app'] = 1

            requestUrl += requestUrl.indexOf('?') === -1 ? '?' : '&'
            requestUrl += http_build_query(params)

            resolve(requestUrl)
        })
    }

    _validateResponse (response, requestParams, onBeforeResponseValidation)
    {
        return new Promise((resolve, reject) => {

            let onBeforeResponseValidationResult = onBeforeResponseValidation(response)
            if( undefined !== onBeforeResponseValidationResult ) {
                response = onBeforeResponseValidationResult
            }

            if( ! isObject(response) )
            {
                return reject(new APIServiceError('[APIService] Invalid JSON response: "' + response + '"'))
            }

            if(response['oc-vue-response-type'] === 'redirect')
            {
                let redirectResponse = this.onRedirect(response['oc-vue-response'], requestParams, {reject})

                if( redirectResponse !== undefined ) {
                    return resolve(redirectResponse)
                }

                return reject(new APIServiceError('Redirect occurred.', undefined, API_ERROR_TYPE_REDIRECT))
            }

            if(response['oc-vue-response-type'] === 'exception')
            {
                return reject(new APIServiceError(response['oc-vue-response'].message))
            }

            if( response.status === undefined || response.message === undefined )
            {
                return reject(
                    new APIServiceError(
                        'Invalid response',
                        new ResponseDTO('Invalid response', API_RESPONSE_STATUS_ERROR, {}, {}, null, {}, {originalResponse: response})
                    )
                )
            }

            // create ResponseDTO from the raw json response
            response = new ResponseDTO(
                response.message,
                response.status,
                response.data,
                response.errors,
                response.errorCode,
                response.responseHeaders,
                response.additional
            )

            if(response.getStatus() === API_RESPONSE_STATUS_SUCCESS)
            {
                return resolve(response)
            }

            reject(new APIServiceError(response.getMessage(), response, API_ERROR_TYPE_NORMAL))
        })
    }

    /**
     * make sure we always have APIServiceError instance
     *
     * @param  {string|json|Error} exception
     * @return {APIServiceError}
     */
    _getAPIServiceError(exception)
    {
        if( exception instanceof APIServiceError ) {
            return exception
        } else if ( exception instanceof Error ) {
            exception = new APIServiceError(exception.message, undefined, API_ERROR_TYPE_INTERNAL, exception)
        } else {
            exception = new APIServiceError(exception)
        }

        return exception
    }
}
